import * as React from 'react';
import styled from 'styled-components';
import {
  TextArea,
  TextAreaChangeEvent,
  TextAreaFocusEvent,
} from '@progress/kendo-react-inputs';
import SimpleReactValidator from 'simple-react-validator';
import { useState } from 'react';
export type TextAreaProps = {
  onChangeText?: (event: TextAreaChangeEvent) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  label?: string;
  rows?: number;
  placeholder?: string;
  autoSize?: boolean;
  className?: string;
  value?: string;
  validator?: SimpleReactValidator;
  validations?: string;
  permissionId?: string;
  onFocus?: ((event: TextAreaFocusEvent) => void) | undefined;
  readOnly?: boolean | undefined;
  hideStar?: boolean;
  name?: string;
  onClick?: React.MouseEventHandler<HTMLTextAreaElement> | undefined;
  maxLength?: number | undefined;
};
export const TextAreaComponent = ({
  onChangeText,
  label,
  disabled,
  rows,
  placeholder,
  autoSize,
  className,
  validator,
  validations,
  readOnly,
  hideStar,
  name,
  value,
  onClick,
  maxLength,
}: TextAreaProps) => {
  const rowCount = rows ? rows : 3;
  const calculatedMinHeight = `${rowCount * 20 + 18}px`;
  const simpleValidator = React.useRef(validator);
  const [validateField, setValidateField] = useState(false);
  const handleChange = (e: TextAreaChangeEvent) => {
    onChangeText?.(e);
  };
  const handleBlur = e => {
    if (!e?.target?.value && validations) {
      simpleValidator?.current?.showMessageFor(
        label?.toString()! ? label?.toString()! : 'field',
      );
      setValidateField(!validateField);
    }
  };

  return (
    <>
      <MainWrapper style={{ position: 'relative' }}>
        <Wrapper
          className={`${disabled ? 'wrapper-disabled' : ''} ${className}`}
        >
          <div className="label">
            {label}{' '}
            <span className="red-star">
              {validations && !hideStar ? '*' : ''}
            </span>
          </div>
          <div style={{ width: '100%' }}>
            <TextArea
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
              rows={rowCount}
              placeholder={placeholder}
              autoSize={autoSize}
              onClick={onClick}
              readOnly={readOnly}
              name={name}
              value={value}
              maxLength={maxLength}
              style={{ minHeight: calculatedMinHeight, height: 'auto' }}
            />

            <Errortext className="error-text">
              {simpleValidator?.current?.message(
                label?.toString() ? label?.toString() : 'field',
                value?.toString(),
                validations ?? '',
              )}
            </Errortext>
          </div>
        </Wrapper>
      </MainWrapper>
    </>
  );
};
const MainWrapper = styled.div``;
const Wrapper = styled.div`
  font-family: 'Open Sans';

  .red-star {
    color: ${p => p.theme.errorStarRed};
    font-size: 15px;
  }

  TextArea {
    background-color: ${p => p.theme.white};
    border: 1px solid ${p => p.theme.datePickerBorderClr};
    width: 100%;
    border-radius: 5px;
    text-size-adjust: 100%;
    color: ${p => p.theme.datePickerNonActiveInputClr};
    font-family: 'Open Sans';
    font-size: 12px;
    text-overflow: ellipsis;
    overflow: auto;
    font-weight: 400;
    line-height: 20px;
    resize: none;

    ::placeholder {
      color: ${p => p.theme.datePickerNonActiveInputClr};
      font-weight: 400;
      font-size: 12px;
      font-family: 'Open Sans';
      line-height: 20px;
    }
    :focus-visible {
      outline: unset !important;
    }
    :disabled {
      opacity: 1;
      cursor: default;
      border-color: ${p => p.theme.disabledBrdrClr} !important;
      background-color: ${p => p.theme.disabledBgClr} !important;
      color: ${p => p.theme.textFieldDisableTextColor};
      font-family: 'Open Sans';
      &.red-star {
        display: none !important;
      }
    }
  }

  .k-input {
    border-style: none;
  }
  .k-input-inner {
    padding: 10px 12px !important;
    box-sizing: border-box;
  }
  .k-input-inner {
    ::-webkit-scrollbar {
      width: 2px;
      margin-left: 3px;
    }

    ::-webkit-scrollbar-track {
      background: transparent !important;
      border-radius: 4px;
    }

    ::-webkit-scrollbar-thumb {
      width: 2px;
      background: ${p => p.theme.layoutPageScrollThumbColor} !important;
      border-radius: 4px;
    }
  }
  .k-input-solid {
    color: unset;
    background: ${p => p.theme.white};
    background: transparent;
  }
  &.wrapper-disabled {
    .k-input-solid {
      background: ${p => p.theme.disabledBgClr};
    }
  }
  .k-input-solid:focus-within {
    border-color: unset;
    color: unset;
    background-color: unset;
    box-shadow: none;
  }

  /* Chrome, Safari, Edge, Opera */
  TextArea ::-webkit-outer-spin-button,
  TextArea::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .label {
    font-family: 'Open Sans';
    color: ${p => p.theme.textFieldTextColor};
    margin-bottom: 5px;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 400;
    line-height: 19.6px;
    margin-left: 4px;
  }
  .error-text {
    font-size: 13px;
  }

  //Responsive
  @media (min-width: 100px) and (max-width: 319px) {
    TextArea {
      font-size: 12px;
      border-radius: 8px;
      min-height: 50px;
    }
  }
  @media (min-width: 320px) and (max-width: 575px) {
    TextArea {
      font-size: 12px;
      border-radius: 8px;
      min-height: 50px;
    }
  }
  @media (min-width: 576px) and (max-width: 767px) {
    TextArea {
      font-size: 12px;
      border-radius: 8px;
      min-height: 50px;
    }
  }
  @media (min-width: 768px) and (max-width: 991px) {
    TextArea {
      font-size: 12px;
      border-radius: 8px;
      min-height: 50px;
    }
  }
  @media (min-width: 992px) and (max-width: 1023px) {
    TextArea {
      font-size: 12px;
      border-radius: 8px;
      min-height: 50px;
    }
  }
  @media (min-width: 1024px) and (max-width: 1279px) {
    TextArea {
      font-size: 12px;
      border-radius: 8px;
      min-height: 50px;
    }
  }
  @media (min-width: 1280px) and (max-width: 1365px) {
    TextArea {
      font-size: 12px;
      border-radius: 8px;
      min-height: 62px;
    }
  }
  @media (min-width: 1366px) and (max-width: 1439px) {
    TextArea {
      font-size: 12px;
      min-height: 66px;
      border-radius: 10px;
    }
  }
  @media (min-width: 1440px) and (max-width: 1600px) {
    TextArea {
      font-size: 12px;
      min-height: 75px;
      border-radius: 10px;
    }
  }

  //Component Responsive
  @media (min-width: 100px) and (max-width: 319px) {
    input {
      height: 30px;
      font-size: 12px;
    }
  }
  @media (min-width: 320px) and (max-width: 575px) {
    input {
      height: 30px;
      font-size: 12px;
    }
  }
  @media (min-width: 576px) and (max-width: 767px) {
    input {
      font-size: 12px;
    }
  }
  @media (min-width: 768px) and (max-width: 991px) {
    input {
      font-size: 13px;
    }
  }
  @media (min-width: 992px) and (max-width: 1199px) {
    input {
      font-size: 13px;
    }
  }
  @media (min-width: 1200px) and (max-width: 1365px) {
    input {
      font-size: 15px;
    }
  }
  @media (min-width: 1366px) and (max-width: 1448px) {
    input {
      font-size: 16px;
    }
  }
  @media (min-width: 1449px) and (max-width: 1719px) {
    input {
      font-size: 17px;
    }
  }
  @media (min-width: 1720px) and (max-width: 4000px) {
    input {
      font-size: 17px;
    }
  }
`;
const Errortext = styled.span`
  display: block;
  font-size: 13px !important;
  color: ${p => p.theme.errorStarRed};
  padding-top: 4px !important;
  text-align: left;
  font-family: 'Open Sans';
`;
